// components/MomentCard.jsx — Anamoria SPA
// v0.5 — NEW-163 Increment B (2026-10-30): NEW-164 SUPERSEDED — the footer MediaTypeChip is removed
//   (the chip now lives only in the response-card header, GroupMemoryCard; moments are the leader's
//   own entries and never get that treatment). spaceIsGroup prop kept (plumbing only, inert).
//   Non-group spaces were already chip-free → byte-identical.
// v0.4 — NEW-164 (2026-10-26): group-feed media-type chip in the footer (anchor kind, or
//   Note when text-led), gated on the new spaceIsGroup prop; presentational, additive.
// v0.3 — feed_redesign_v2: additive `redesign` prop → .cardV2 bordered chrome
//   when set (feed). Falsy elsewhere ⇒ byte-identical; MomentDetailPage
//   unaffected. Embedded inner Voice/Video cards not given the prop. (July 27, 2026)
// v0.2 — 3.2 navigation (July 21, 2026)
//
// Changes from v0.1 (momentdetail-3-2-DRAFT-for-review-v0.3.md E-4):
//   - NEW optional prop `onNavigate`. When present the root <article> becomes
//     a link target: role="link", tabIndex=0, onClick + Enter keydown (APG
//     link semantics — Enter, not Space; v0.2 §E E-5), styles.interactive
//     (cursor/hover tint/focus ring, .css v0.5, D-3.18). Concise accessible
//     name stays the title (mitigates block-link verbosity, Inclusive
//     Components "Cards" / F-E1). When absent: rendering byte-identical to
//     v0.1 (3.1 inertness preserved by construction).
//   - MomentPhoto now exported (named) for MomentDetailPage v0.1.
//   - Nested targets stay distinct: VideoCard pill / VoiceCard play already
//     stopPropagation (verified v1.5 L149/L172; v2.11 L216–217).
//
// v0.1 — One card per moment (Phase 3 step 3.1; §3a, D-3.3, D-3.13..15)
//
//   - One visual anchor, never two (§3a): presence (video → voice) else photo,
//     first by piece_order (server pre-sorts pieces, anamoria-memories v2.18).
//   - Presence slot reuses themed VoiceCard v2.11 / VideoCard v1.4 with the
//     `embedded` prop: theme + player + waveform/poster only; overlay, root
//     button semantics, own title and own footer are conditionally NOT
//     rendered (D-3.14 grounds — no ghost controls, no duplicated chrome).
//   - Title always: stored title, else render-time auto-title (momentTitle.js);
//     undated on cards — the card carries its own date line (copy v0.4 §2).
//   - Text preview tiers (§3a CONFIRMED): lead ~6 / under photo ~3 / under
//     presence ~2 lines, via line-clamp in MomentCard.module.css.
//   - Date: dayText else "Month Year". Attribution mirrors MemoryFeed
//     formatCardAttribution using moment.creatorType + first piece creatorName
//     (moment entries carry no creatorName of their own — F-6).
//   - INERT at 3.1 (D-3.13): no onClick, no role, no tabIndex, no hover
//     affordance, cursor default. Navigation arrives with 3.2.
//   - Cover override (§3a) dormant: no promote UI exists yet; default ladder only.

import VoiceCard from './VoiceCard';
import VideoCard from './VideoCard';
import BlurHashCanvas from './BlurHashCanvas';
import {
  selectAnchor,
  firstTextPiece,
  deriveMomentTitle,
  momentDateLabel,
} from '../utils/momentTitle';
import styles from './MomentCard.module.css';

/* Local copy of the shared privacy glyph (precedent: VoiceCard/VideoCard each
   carry their own; importing from MemoryFeed would be a circular import). */
function PrivacyIcon({ isPrivate }) {
  if (isPrivate) {
    return (
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#737373" strokeWidth="1.5" strokeLinecap="round">
        <rect x="5" y="11" width="14" height="10" rx="2" />
        <path d="M8 11V7a4 4 0 0 1 8 0v4" />
      </svg>
    );
  }
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#737373" strokeWidth="1.5" strokeLinecap="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
  );
}

/* Photo anchor — mirrors the legacy photo card image pipeline (signed URL,
   feed-variant onError fallback, stored dims → aspect-ratio, BlurHash).
   Always lazy at 3.1 (F-4: legacy first-photo fetchpriority preserved on the
   legacy branch; moment photos join that logic in a later pass if LCP asks). */
export function MomentPhoto({ piece, alt, onImageLoad }) {
  const photoUrl = piece.playbackUrl;
  const fallbackUrl = piece.originalPlaybackUrl;
  const aspectStyle = piece.imageWidth && piece.imageHeight
    ? { aspectRatio: `${piece.imageWidth} / ${piece.imageHeight}` }
    : { aspectRatio: '4 / 3' };
  const blurWidth = 269;
  const blurHeight = piece.imageWidth && piece.imageHeight
    ? Math.round((blurWidth * piece.imageHeight) / piece.imageWidth)
    : 180;

  return (
    <div className={styles.photoWrap} style={aspectStyle}>
      {photoUrl ? (
        <img
          className={styles.photoImage}
          src={photoUrl}
          alt={alt}
          loading="lazy"
          onLoad={onImageLoad}
          onError={(e) => {
            if (fallbackUrl && e.target.src !== fallbackUrl) {
              e.target.src = fallbackUrl;
            }
          }}
        />
      ) : piece.blurHash ? (
        <BlurHashCanvas hash={piece.blurHash} width={blurWidth} height={blurHeight} />
      ) : (
        <div className={styles.photoSkeleton} />
      )}
    </div>
  );
}

export default function MomentCard({ moment, voiceCardTheme, getApi, onImageLoad, onNavigate, redesign = false, spaceIsGroup = false }) {
  const pieces = moment.pieces || [];
  const anchor = selectAnchor(pieces);
  const textPiece = firstTextPiece(pieces);
  const title = deriveMomentTitle(moment); // undated on cards (copy v0.4 §2)
  const dateLabel = momentDateLabel(moment);

  // F-6: attribution — moment-level creatorType, name from the first piece
  // that carries one (pieces are mapMemoryRow-shaped and include creatorName).
  const creatorName =
    moment.creatorType === 'contributor'
      ? (pieces.find((p) => p.creatorName) || {}).creatorName
      : null;
  const attribution = creatorName ? `${creatorName} · ${dateLabel}` : dateLabel;

  // §3a text tiers: lead (no anchor) / under photo / under presence.
  const previewClass = !anchor
    ? styles.previewLead
    : anchor.kind === 'photo'
      ? styles.previewUnderPhoto
      : styles.previewUnderPresence;

  // Presence-anchored cards are transparent containers (the themed inner card
  // is the chrome, photo-card pattern); artifact-led cards carry the white
  // textCard-style chrome. See MomentCard.module.css.
  const cardClass = anchor && anchor.kind !== 'photo' ? styles.cardPresence : styles.card;

  // 3.2 (F-E1): navigable when the feed provides onNavigate — role="link"
  // (Enter activates, not Space — APG link semantics). Inert when the prop
  // is absent: identical to the v0.1 rendering by construction (D-3.13).
  const interactive = typeof onNavigate === 'function';
  const handleKeyDown = interactive
    ? (e) => { if (e.key === 'Enter') onNavigate(moment); }
    : undefined;

  return (
    <article
      className={`${cardClass}${interactive ? ` ${styles.interactive}` : ''}${redesign ? ` ${styles.cardV2}` : ''}`}
      aria-label={title}
      role={interactive ? 'link' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onClick={interactive ? () => onNavigate(moment) : undefined}
      onKeyDown={handleKeyDown}
    >
      {anchor && anchor.kind === 'video' && (
        <div className={styles.presenceSlot}>
          <VideoCard memory={anchor.piece} embedded />
        </div>
      )}
      {anchor && anchor.kind === 'voice' && (
        <div className={styles.presenceSlot}>
          <VoiceCard
            memory={anchor.piece}
            getApi={getApi}
            theme={voiceCardTheme || 'warm'}
            embedded
            redesign={redesign}
          />
        </div>
      )}
      {anchor && anchor.kind === 'photo' && (
        <MomentPhoto piece={anchor.piece} alt={title} onImageLoad={onImageLoad} />
      )}

      <div className={styles.content}>
        <p className={styles.title}>{title}</p>
        {textPiece && (
          <p className={`${styles.preview} ${previewClass}`}>{textPiece.note}</p>
        )}
      </div>

      <div className={styles.footer}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span className={styles.date}>{attribution}</span>
        </span>
        <span className={styles.privacy}>
          <PrivacyIcon isPrivate={moment.isPrivate} />
        </span>
      </div>
    </article>
  );
}
